import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router';

import { AppShell } from '@/app/layout/app-shell';
import { RequireAuth } from '@/features/auth/components/require-auth';
import { RequireRole } from '@/features/auth/components/require-role';
import { ADMIN_ROLES, SUPERADMIN_ROLES } from '@/features/auth/permissions';
import { AcceptInvitationPage } from '@/features/auth/pages/accept-invitation-page';
import { ForgotPasswordPage } from '@/features/auth/pages/forgot-password-page';
import { LoginPage } from '@/features/auth/pages/login-page';
import { ResetPasswordPage } from '@/features/auth/pages/reset-password-page';
import type { AppRole } from '@/features/auth/types';
import { NotFoundPage } from '@/features/system/pages/not-found-page';

import { RouteErrorBoundary } from './route-error-boundary';

const DashboardPage = lazy(() =>
  import('@/features/dashboard/pages/dashboard-page').then((m) => ({ default: m.DashboardPage })),
);
const SaasProductsPage = lazy(() =>
  import('@/features/saas/pages/saas-products-page').then((m) => ({
    default: m.SaasProductsPage,
  })),
);
const CustomersPage = lazy(() =>
  import('@/features/customers/pages/customers-page').then((m) => ({ default: m.CustomersPage })),
);
const CustomerDetailPage = lazy(() =>
  import('@/features/customers/pages/customer-detail-page').then((m) => ({
    default: m.CustomerDetailPage,
  })),
);
const BillingDocumentsPage = lazy(() =>
  import('@/features/billing/pages/billing-documents-page').then((m) => ({
    default: m.BillingDocumentsPage,
  })),
);
const BillingDocumentPage = lazy(() =>
  import('@/features/billing/pages/billing-document-page').then((m) => ({
    default: m.BillingDocumentPage,
  })),
);
const BillingSeriesPage = lazy(() =>
  import('@/features/billing/pages/billing-series-page').then((m) => ({
    default: m.BillingSeriesPage,
  })),
);
const BoardsPage = lazy(() =>
  import('@/features/work/pages/boards-page').then((m) => ({ default: m.BoardsPage })),
);
const BoardPage = lazy(() =>
  import('@/features/work/pages/board-page').then((m) => ({ default: m.BoardPage })),
);
const IntegrationsPage = lazy(() =>
  import('@/features/integrations/pages/integrations-page').then((m) => ({
    default: m.IntegrationsPage,
  })),
);
const EmailPage = lazy(() =>
  import('@/features/email/pages/email-page').then((m) => ({ default: m.EmailPage })),
);
const MailboxesPage = lazy(() =>
  import('@/features/email/pages/mailboxes-page').then((m) => ({ default: m.MailboxesPage })),
);
const AgendaPage = lazy(() =>
  import('@/features/agenda/pages/agenda-page').then((m) => ({ default: m.AgendaPage })),
);
// Public guest page lives outside the shell, so it brings its own Suspense boundary.
const MeetingGuestPage = lazy(() =>
  import('@/features/agenda/pages/meeting-guest-page').then((m) => ({
    default: m.MeetingGuestPage,
  })),
);
const UsersPage = lazy(() =>
  import('@/features/users/pages/users-page').then((m) => ({ default: m.UsersPage })),
);

function withRole(roles: readonly AppRole[], element: ReactNode) {
  return <RequireRole roles={roles}>{element}</RequireRole>;
}

export const routes: RouteObject[] = [
  // Public (no registration route exists by design).
  { path: '/login', element: <LoginPage /> },
  { path: '/recuperar-contrasena', element: <ForgotPasswordPage /> },
  { path: '/restablecer-contrasena', element: <ResetPasswordPage /> },
  // Onboarding for accounts created by a SUPERADMIN invitation (not signup).
  { path: '/aceptar-invitacion', element: <AcceptInvitationPage /> },
  // External meeting guests (no ERP account): the invitation token is the credential.
  {
    path: '/reunion/:token',
    element: (
      <Suspense fallback={null}>
        <MeetingGuestPage />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      {
        // Pathless boundary keeps the shell mounted when a page fails.
        errorElement: <RouteErrorBoundary />,
        element: <Outlet />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: 'dashboard', element: <DashboardPage /> },
          { path: 'saas', element: <SaasProductsPage /> },
          { path: 'clientes', element: <CustomersPage /> },
          { path: 'clientes/:customerId', element: <CustomerDetailPage /> },
          { path: 'facturacion', element: withRole(ADMIN_ROLES, <BillingDocumentsPage />) },
          {
            path: 'facturacion/series',
            element: withRole(SUPERADMIN_ROLES, <BillingSeriesPage />),
          },
          {
            path: 'facturacion/:documentId',
            element: withRole(ADMIN_ROLES, <BillingDocumentPage />),
          },
          { path: 'trabajo', element: <BoardsPage /> },
          // Workspace pages use the full width of the viewport (see AppShell).
          { path: 'trabajo/:boardId', element: <BoardPage />, handle: { fullWidth: true } },
          // Mail client layout; access to each mailbox is enforced by RLS membership.
          { path: 'correos', element: <EmailPage />, handle: { fullWidth: true } },
          { path: 'correos/:mailboxId', element: <EmailPage />, handle: { fullWidth: true } },
          {
            path: 'correos/:mailboxId/:threadId',
            element: <EmailPage />,
            handle: { fullWidth: true },
          },
          { path: 'bandejas', element: withRole(ADMIN_ROLES, <MailboxesPage />) },
          // Zoho Calendar mirror + client meetings; calendar visibility is enforced by RLS.
          { path: 'agenda', element: <AgendaPage />, handle: { fullWidth: true } },
          {
            path: 'configuracion/integraciones',
            element: withRole(SUPERADMIN_ROLES, <IntegrationsPage />),
          },
          {
            path: 'configuracion/usuarios',
            element: withRole(SUPERADMIN_ROLES, <UsersPage />),
          },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];
